/**
 * One-click status control shared by Kunden and Posteingang (extracted from StatusSelect).
 * Holds the pick until `value` catches up, reverts and shows the error on failure.
 * Clicks stop propagation, since both hosts sit in a clickable row.
 */

import { useEffect, useState } from 'react';
import { StatusDot, type StatusTone } from './StatusDot';
import { cn } from '../../lib/cn';

export interface StatusDropdownOption<T extends string> {
  value: T;
  label: string;
}

interface StatusDropdownProps<T extends string> {
  value: T;
  options: readonly StatusDropdownOption<T>[];
  /** Rejects when the write fails; the caller does not need to handle the error. */
  onChange: (next: T) => Promise<unknown>;
  /** Label + tone of the current value, for the read-only rendering. */
  current: { label: string; tone: StatusTone };
  /** Render the current value as a dot + label only, with `readOnlyTitle` as tooltip. */
  readOnly?: boolean;
  readOnlyTitle?: string;
  ariaLabel: string;
  errorMessage?: string;
  className?: string;
}

export function StatusDropdown<T extends string>({
  value,
  options,
  onChange,
  current,
  readOnly = false,
  readOnlyTitle,
  ariaLabel,
  errorMessage = 'Status konnte nicht geändert werden.',
  className,
}: StatusDropdownProps<T>) {
  const [picked, setPicked] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Drop the optimistic value once the incoming value has caught up with it.
  useEffect(() => {
    if (picked !== null && value === picked) setPicked(null);
  }, [value, picked]);

  if (readOnly) {
    return (
      <span className={className} title={readOnlyTitle}>
        <StatusDot tone={current.tone} label={current.label} />
      </span>
    );
  }

  async function apply(next: T) {
    setPicked(next);
    setError(null);
    try {
      await onChange(next);
    } catch {
      setPicked(null);
      setError(errorMessage);
    }
  }

  return (
    <span className={cn('inline-flex flex-col items-start gap-1', className)}>
      <select
        aria-label={ariaLabel}
        value={picked ?? value}
        // The host row is clickable (it navigates), so the pick must not reach it.
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => void apply(e.target.value as T)}
        className="h-8 rounded-md border border-border bg-background px-2 text-sm text-foreground outline-none transition-colors hover:border-foreground/30 focus-visible:ring-2 focus-visible:ring-ring"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {error !== null && (
        <span role="alert" className="text-xs text-status-danger">
          {error}
        </span>
      )}
    </span>
  );
}
